import { Anim, AnimPlayer, Ease, lerp } from "../animation";
import { HorizontalCarousel } from "../infinite-scroller";

const WINDOW_PADDING_X = 0.01;
const WINDOW_PADDING_Y = WINDOW_PADDING_X * 1.6;
const CLASSES = { pressed: "pressed", selected: "selected", smBar: "sm-bar" }

type Area = {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

type WindowState = Area & { opacity: number };

class Window {

  readonly element: HTMLElement;
  private state: WindowState = { left: 0, top: 0, right: 0, bottom: 0, opacity: 0 };
  

  constructor(element: HTMLElement | null = null) {
    if (element) {
      this.element = element;
    } else {
      this.element = element ?? document.createElement("div");
      this.element.classList.add("window-area");
    }
  }

  appendTo(parent: HTMLElement) {
    parent.appendChild(this.element);
  }

  setState(state: WindowState) {
    this.state = state;
    const area = Window.padArea(state);
    this.element.style.left = `${area.left * 100}%`;
    this.element.style.right = `${area.right * 100}%`;
    this.element.style.top = `${area.top * 100}%`;
    this.element.style.bottom = `${area.bottom * 100}%`;
    this.element.style.widows = "";
    this.element.style.height = "";
    this.element.style.opacity = `${state.opacity}`;
  }

  getState() {
    return this.state;
  }

  lerp(from: WindowState, to: WindowState) {
    return (p: number) => {
      this.setState({
        left: lerp(from.left, to.left, p),
        top: lerp(from.top, to.top, p),
        right: lerp(from.right, to.right, p),
        bottom: lerp(from.bottom, to.bottom, p),
        opacity: lerp(from.opacity, to.opacity, p)
      });
    };
  }

  center() {
    const domRect = this.element.getBoundingClientRect();
    const parentDomRect = this.element.parentElement!.getBoundingClientRect();
    this.setPosition(
      (parentDomRect.width - domRect.width) / 2,
      (parentDomRect.height - domRect.height) / 2
    )
  }

  setPosition(x: number, y: number) {
    this.element.style.left = `${x}px`;
    this.element.style.top = `${y}px`;
  }

  static getMouseEventPoint(event: MouseEvent) {
    return { x: event.pageX, y: event.pageY };
  }

  static padArea(area: Area): Area {
    const left = Window.padValue(area.left, WINDOW_PADDING_X);
    const right = Window.padValue(area.right, WINDOW_PADDING_X);
    const top = Window.padValue(area.top, WINDOW_PADDING_Y);
    const bottom = Window.padValue(area.bottom, WINDOW_PADDING_Y);
    return { left, top, right, bottom };
  }

  static padValue(value: number, padding: number) {
    return value === 0 ? padding : value + padding / 2;
  }
}

class WindowManagerKeybinds {

  private window: Window;
  private button: HTMLButtonElement;
  private autoRelease = true;

  private windowStates: WindowState[];
  private windowStateLabels: string[];

  private windowStateIndex = 0;
  private player = new AnimPlayer();

  constructor(parent: HTMLElement) {
    setInterval(() => this.pressButton(), 1000);

    this.button = parent.querySelector("button")!;
    this.button.addEventListener("mousedown", () => this.pressButton(false));
    this.button.addEventListener("mouseup", () => this.releaseButton(true));

    this.windowStateLabels = ["1 / 2", "1 / 3", "1 / 4"]
    this.windowStates = [
      { top: 0, bottom: 0, left: 1/2, right: 0, opacity: 1 },
      { top: 0, bottom: 0, left: 2/3, right: 0, opacity: 1 },
      { top: 0, bottom: 0, left: 3/4, right: 0, opacity: 1 },
    ]

    this.window = new Window(parent.querySelector<HTMLElement>(".window-area")!)
    this.window.element.textContent = this.windowStateLabels[0];
    this.window.setState(this.windowStates[0]);
  }

  private pressButton(autoRelease: boolean | null = null) {
    if (autoRelease !== null) this.autoRelease = autoRelease;
    this.button.classList.add(CLASSES.pressed);
    this.lerpToNextWindowState();
    
    if (this.autoRelease) {
      setTimeout(() => this.releaseButton(), 200);
    }
  }

  private releaseButton(autoRelease: boolean | null = null) {
    if (autoRelease !== null) this.autoRelease = autoRelease;
    if (this.autoRelease) {
      this.button.classList.remove(CLASSES.pressed)
    }
  }

  private lerpToNextWindowState() {
    const nextIndex = (this.windowStateIndex + 1) % this.windowStates.length;
    this.player.play(
      Anim.sequence(
        Anim.instant(() => {
          this.window.element.textContent = 
            this.windowStateLabels[nextIndex];
        }),
        Anim.ease(200, Ease.cubic,
          this.window.lerp(
            this.window.getState(), 
            this.windowStates[nextIndex]
          )
        )
      )
    );
    this.windowStateIndex = nextIndex;
  }
}

class WindowManagerMonitorSetup {
  constructor(parent: HTMLElement, states: WindowState[]) {
    for (const state of states) {
      const window = new Window();
      window.appendTo(parent);
      window.setState(state);
    }
  }
}

class BarChart {

  private selectionDir = 1;

  constructor(private parent: HTMLElement) {
    this.populate();
    const observer = new ResizeObserver(() => this.onResize());
    observer.observe(parent);
  }

  private onResize() {
    this.populate();
    this.removeOldBars();
  }

  private populate() {
    while(!this.isPopulated()) {
      this.addBar();
    }
  }

  addNewBar() {
    this.addBar();
    this.removeOldBars();
  }

  moveSelection() {
    const iterate = () => {
      const selected = this.parent.querySelector(`.${CLASSES.selected}`);
      
      if (!selected) {
        const firstChild = this.parent.firstElementChild;
        if (!firstChild) return;
        firstChild.classList.add(CLASSES.selected);
        return;
      }

      const forward = this.selectionDir > 0 ? 
        selected.nextElementSibling : 
        selected.previousElementSibling;

      const backgward = this.selectionDir > 0 ? 
        selected.previousElementSibling : 
        selected.nextElementSibling;

      if (forward) {
        selected.classList.remove(CLASSES.selected);
        forward.classList.add(CLASSES.selected);
      } else if (backgward) {
        this.selectionDir *= -1;
        iterate();
      }
    }

    iterate();
  }

  private isPopulated() {
    const firstChild = this.parent.firstElementChild;
    if (!firstChild) return false;
    const parentRect = this.parent.getBoundingClientRect();
    const firstRect = firstChild.getBoundingClientRect();
    return firstRect.left <= parentRect.left + firstRect.width / 2;
  }

  private addBar() {
    const bar = document.createElement("div");
    bar.classList.add(CLASSES.smBar);
    this.parent.append(bar);
    
    const userBar = document.createElement("div");
    userBar.style.height = `${BarChart.smoothAverage() * 100}%`;
    bar.append(userBar);

    const systemBar = document.createElement("div");
    systemBar.style.height = `${BarChart.smoothAverage() * 100}%`;
    userBar.append(systemBar);
  }

  private removeOldBars() {
    const parentRect = this.parent.getBoundingClientRect();
    for (let index = 0; index < this.parent.childElementCount; index++) {
      const bar = this.parent.children.item(index)!;
      const rect = bar.getBoundingClientRect();
      if (rect.right < parentRect.left || rect.right <= 0) {
        bar.remove();
      } else {
        break;
      }
    }
  }

  static smoothAverage(count = 10) {
    let sum = 0;
    for (let index = 0; index < count; index++) {
      sum += Math.random();
    }
    return sum / count;
  }
}

class Slider {

  private track: HTMLElement;
  private bar: HTMLElement;
  private knob: HTMLElement;

  constructor(element: Element, initialValue = 1, private onChange: (value: number) => void) {
    this.track = element.querySelector(".track")!;
    this.bar = element.querySelector(".bar")!;
    this.knob = element.querySelector(".knob")!;
    this.setValue(initialValue);

    this.knob.addEventListener("pointerdown", () => {
      const trackRect = this.track.getBoundingClientRect();

      const onTouchMove = (event: TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
      }
      
      const onMove = (event: MouseEvent) => {
        const value = Math.min(1, Math.max(0, (event.clientX - trackRect.x) / trackRect.width));
        this.setValue(value);
        event.preventDefault();
        event.stopPropagation();
      }
      
      const onUp = () => {
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      }
      
      document.addEventListener("touchmove", onTouchMove);
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    });
  }
  
  private setValue(value: number) {
    const progress = `${value * 100}%`;
    this.bar.style.width = progress;
    this.knob.style.left = progress;
    this.onChange(value);
  }
}

class BrightnessControl {
  private shade: HTMLElement;

  constructor(element: Element) {
    this.shade = element.querySelector(".shade")!;
    new Slider(element, 1, (value) => this.updateShade(value));
  }

  private updateShade(value: number) {
    this.shade.style.opacity = `${(1 - value) * 0.8}`;
  }
}

type SystemMonitoringFeature = {
  title: string;
  description: string;
  timestamp: number;
};

class SystemMonitoringShowcase {
  private readonly video: HTMLVideoElement;
  private readonly title: HTMLElement;
  private readonly description: HTMLElement;
  private readonly features: SystemMonitoringFeature[] = [
    {
      title: "Organized View",
      description: "Child processes are grouped under their logical parent, and usage is aggregated, to give you the complete picture.",
      timestamp: 0,
    },
    {
      title: "Disk Clean",
      description: "Free up disk space with one click. OneMenu scans for large files, caches, and junk so you can reclaim storage fast.",
      timestamp: 10.75,
    },
    {
      title: "System Stats",
      description: "Monitor temperature, per-core CPU utilization, GPU usage, memory, network, and disk activity from one compact dashboard.",
      timestamp: 25.25,
    },
  ];
  private index = 0;

  constructor(element: HTMLElement) {
    this.video = element.querySelector("video")!;
    this.title = element.querySelector("h3")!;
    this.description = element.querySelector("p")!;

    element.querySelector(".previous")!.addEventListener("click", () => this.select(this.index - 1));
    element.querySelector(".next")!.addEventListener("click", () => this.select(this.index + 1));
    this.video.addEventListener("timeupdate", () => this.syncToVideo());
  }

  private select(index: number) {
    this.index = (index + this.features.length) % this.features.length;
    const feature = this.features[this.index];
    this.render(feature);
    this.video.currentTime = feature.timestamp;
    void this.video.play();
  }

  private syncToVideo() {
    let nextIndex = 0;
    for (let index = 1; index < this.features.length; index++) {
      if (this.video.currentTime < this.features[index].timestamp) break;
      nextIndex = index;
    }
    if (nextIndex === this.index) return;

    this.index = nextIndex;
    this.render(this.features[this.index]);
  }

  private render(feature: SystemMonitoringFeature) {
    this.title.textContent = feature.title;
    this.description.textContent = feature.description;
  }
}

new WindowManagerKeybinds(document.querySelector("#window-manager-keybinds")!);

new WindowManagerMonitorSetup(document.querySelector("#monitor-setup .ultrawide-monitor")!, [
  { top: 0, bottom: 0, left: 0, right: 4/5, opacity: 1 },
  { top: 0, bottom: 0, left: 1/5, right: 1/5, opacity: 1 },
  { top: 0, bottom: 0, left: 4/5, right: 0, opacity: 1 },
]);

new WindowManagerMonitorSetup(document.querySelector("#monitor-setup .macbook-pro .workspace")!, [
  { top: 0, bottom: 0, left: 0, right: 1/2, opacity: 1 },
  { top: 0, bottom: 1/2, left: 1/2, right: 0, opacity: 1 },
  { top: 1/2, bottom: 0, left: 1/2, right: 0, opacity: 1 },
]);

document.querySelectorAll<HTMLElement>(".horizontal-carousel").forEach(element => {
  new HorizontalCarousel(element, { speed: 0.02 });
});

new BarChart(document.querySelector("#system-monitoring-intro .sm-bar-chart")!);
new SystemMonitoringShowcase(document.querySelector("#system-monitoring")!);
new BrightnessControl(document.querySelector("#brightness-control")!);
